import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { getAdminReports } from "../../business/AdminBusiness";
import { getAllUsers } from "../../business/UserBusiness";
import { AppShell, headerButtonClass } from '../../components/Layout/AppShell';
import { UserDetailsDrawer } from '../../components/UserDetailsDrawer';
import { Panel } from '../../components/ui/Panel';
import { StatTile } from '../../components/ui/StatTile';
import { SegmentedTabs } from '../../components/ui/SegmentedTabs';
import { Avatar } from '../../components/ui/Avatar';
import { BarChart, HBarList } from '../../components/ui/BarChart';
import { PLAN_META, getEffectivePlan } from '../../lib/planMeta';
import {
  BUSINESS_AREA_LABELS, ACQUISITION_LABELS, isPayingSubscriber, formatCurrency, formatNumber,
} from '../../lib/userInsights';
import {
  BarChart3, Wallet, Receipt, CalendarDays, TrendingUp, Users, BadgeCheck, Trophy, RefreshCw, CalendarRange,
  UserPlus, Shapes, Megaphone, PieChart,
} from "lucide-react";
import toast from "react-hot-toast";

const PERIODS = [
  { key: 'all', label: 'Todo período' },
  { key: 'today', label: 'Hoje' },
  { key: '7d', label: '7 dias' },
  { key: '30d', label: '30 dias' },
  { key: '6m', label: '6 meses' },
  { key: 'year', label: 'Este ano' },
  { key: 'custom', label: 'Personalizado' },
];

const AREA_COLORS = ['#6D4AFE', '#8B6CFF', '#A78BFA', '#C4B5FD', '#7DD3FC', '#38BDF8', '#34D399', '#FBBF24', '#FB923C', '#F472B6', '#94A3B8', '#CBD5E1', '#E2E8F0'];
const SOURCE_COLORS = { instagram: '#E1306C', facebook: '#1877F2', google: '#34A853', indicacao: '#6D4AFE', outro: '#94A3B8' };

const toInputDate = (d) => d.toLocaleDateString('en-CA');

const periodRange = (key) => {
  const today = new Date();
  const start = new Date(today);
  if (key === 'today') return [toInputDate(today), toInputDate(today)];
  if (key === '7d') start.setDate(today.getDate() - 6);
  else if (key === '30d') start.setDate(today.getDate() - 29);
  else if (key === '6m') start.setMonth(today.getMonth() - 6);
  else if (key === 'year') return [toInputDate(new Date(today.getFullYear(), 0, 1)), toInputDate(today)];
  else return ['', ''];
  return [toInputDate(start), toInputDate(today)];
};

const shortDay = (iso) => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const compactCurrency = (v) =>
  v >= 1000 ? `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil` : formatCurrency(v).replace(',00', '');

// Ranking com barra proporcional atrás de cada linha
const RankingPanel = ({ icon, title, subtitle, items, valueOf, renderValue, onItemClick, emptyLabel }) => {
  const max = Math.max(1, ...items.map(valueOf));
  return (
    <Panel icon={icon} title={title} subtitle={subtitle} bodyClassName="p-2">
      {items.length ? (
        <ol>
          {items.map((item, index) => (
            <li key={item._id || index}>
              <button
                type="button"
                onClick={() => onItemClick(item)}
                className="relative flex w-full items-center gap-3 overflow-hidden rounded-xl px-3 py-2.5 text-left transition hover:bg-subtle"
              >
                <span
                  className="absolute inset-y-1 left-0 rounded-r-xl bg-accent-light/70"
                  style={{ width: `${(valueOf(item) / max) * 100}%` }}
                />
                <span className={`tabular relative w-5 text-center text-xs font-semibold ${index < 3 ? 'text-accent' : 'text-gray-400'}`}>{index + 1}</span>
                <span className="relative"><Avatar name={item.username} size="sm" /></span>
                <span className="relative min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">{item.username || 'Sem nome'}</span>
                  <span className="block truncate text-xs text-gray-500">{item.businessName || '—'}</span>
                </span>
                <span className="relative flex-shrink-0 text-right">{renderValue(item)}</span>
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <p className="py-10 text-center text-sm text-gray-400">{emptyLabel}</p>
      )}
    </Panel>
  );
};

export const Reports = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customOpen, setCustomOpen] = useState(false);
  const [chartMetric, setChartMetric] = useState('revenue');
  const [reportData, setReportData] = useState(null);
  const [users, setUsers] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const customRef = useRef(null);

  const fetchData = useCallback(async (sd, ed) => {
    try {
      setIsLoading(true);
      const params = {};
      if (sd && ed) {
        params.start_date = sd;
        params.end_date = ed;
      }
      const [reportsRes, usersRes] = await Promise.all([getAdminReports(params), getAllUsers()]);
      setReportData(reportsRes?.data || null);
      setUsers(usersRes?.data || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Erro ao buscar relatórios:', error);
      toast.error('Erro ao carregar relatórios');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData('', ''); }, [fetchData]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customRef.current && !customRef.current.contains(e.target)) setCustomOpen(false);
    };
    if (customOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [customOpen]);

  const handlePeriod = (key) => {
    if (key === 'custom') {
      setCustomOpen(true);
      return;
    }
    const [sd, ed] = periodRange(key);
    setPeriod(key);
    setStartDate(sd);
    setEndDate(ed);
    setCustomOpen(false);
    fetchData(sd, ed);
  };

  const applyCustom = (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error('Escolha as duas datas');
      return;
    }
    if (startDate > endDate) {
      toast.error('A data inicial precisa ser antes da final');
      return;
    }
    setPeriod('custom');
    setCustomOpen(false);
    fetchData(startDate, endDate);
  };

  const isPeriodFiltered = period !== 'all';
  const periodLabel = isPeriodFiltered
    ? startDate === endDate ? shortDay(startDate) : `${shortDay(startDate)} a ${shortDay(endDate)}`
    : 'desde o início';

  const {
    usersByPlan = {}, totalTransacted = 0, totalTransactions = 0, totalAppointments = 0,
    topTransactors = [], topByAppointments = [], timeline,
  } = reportData || {};

  // Tudo que sai da lista de usuários (cadastros, áreas, origem, assinantes)
  const userStats = useMemo(() => {
    const start = isPeriodFiltered && startDate ? new Date(`${startDate}T00:00:00`) : null;
    const end = isPeriodFiltered && endDate ? new Date(`${endDate}T23:59:59.999`) : null;
    const inPeriod = users.filter((u) => {
      const t = new Date(u.createdAt);
      return (!start || t >= start) && (!end || t <= end);
    });

    // Cadastros por semana — últimas 12 semanas, começando na segunda
    const weekStart = new Date();
    weekStart.setHours(0, 0, 0, 0);
    weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
    const weeks = Array.from({ length: 12 }, (_, i) => {
      const from = new Date(weekStart);
      from.setDate(from.getDate() - (11 - i) * 7);
      const to = new Date(from);
      to.setDate(to.getDate() + 7);
      const created = users.filter((u) => { const t = new Date(u.createdAt); return t >= from && t < to; });
      return {
        label: `${String(from.getDate()).padStart(2, '0')}/${String(from.getMonth() + 1).padStart(2, '0')}`,
        tooltipLabel: `Semana de ${from.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '')}`,
        signups: created.length,
        paying: created.filter(isPayingSubscriber).length,
      };
    });

    const areaCounts = {};
    const sourceCounts = {};
    inPeriod.forEach((u) => {
      (u.businessAreas || []).forEach((a) => { areaCounts[a] = (areaCounts[a] || 0) + 1; });
      const source = u.referralCodeUsed ? 'indicacao' : u.acquisitionSource;
      if (source) sourceCounts[source] = (sourceCounts[source] || 0) + 1;
    });

    return {
      total: users.length,
      newInPeriod: inPeriod.length,
      paying: users.filter(isPayingSubscriber).length,
      payingInPeriod: inPeriod.filter(isPayingSubscriber).length,
      weeks,
      withArea: inPeriod.filter((u) => u.businessAreas?.length).length,
      withSource: inPeriod.filter((u) => u.referralCodeUsed || u.acquisitionSource).length,
      areas: Object.entries(areaCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([key, value], i) => ({ key, value, label: BUSINESS_AREA_LABELS[key] || key, color: AREA_COLORS[i % AREA_COLORS.length] })),
      sources: Object.entries(sourceCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([key, value]) => ({ key, value, label: ACQUISITION_LABELS[key] || key, color: SOURCE_COLORS[key] || '#94A3B8' })),
    };
  }, [users, isPeriodFiltered, startDate, endDate]);

  // Plano por usuária vem do backend (mesma regra do getEffectivePlan); se o backend ainda não
  // tiver mandado algum plano, cai na contagem feita aqui com a lista.
  const planItems = useMemo(() => {
    const local = {};
    users.forEach((u) => { const p = getEffectivePlan(u); local[p] = (local[p] || 0) + 1; });
    return Object.keys(PLAN_META).map((plan) => ({
      key: plan,
      label: PLAN_META[plan].label,
      value: usersByPlan[plan] ?? local[plan] ?? 0,
      color: plan === 'free' ? '#CBD5E1' : PLAN_META[plan].color,
    }));
  }, [users, usersByPlan]);
  const planTotal = planItems.reduce((a, b) => a + b.value, 0);

  const chartData = (timeline || []).map((d) => ({ ...d, label: shortDay(d.date), tooltipLabel: new Date(`${d.date}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' }).replace(/\./g, '') }));
  const ticket = totalTransactions > 0 ? totalTransacted / totalTransactions : 0;
  const conversion = userStats.total ? Math.round((userStats.paying / userStats.total) * 100) : 0;

  return (
    <>
      <AppShell
        icon={BarChart3}
        eyebrow="Visão geral"
        title="Relatórios"
        meta={lastUpdated ? <>Período: <span className="font-semibold text-gray-700">{periodLabel}</span> · atualizado às {lastUpdated.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</> : 'Carregando...'}
        actions={
          <button className={headerButtonClass} onClick={() => fetchData(isPeriodFiltered ? startDate : '', isPeriodFiltered ? endDate : '')} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} /> Atualizar
          </button>
        }
      >
        <div className="space-y-5">
          <div className="relative flex flex-wrap items-center gap-3" ref={customRef}>
            <CalendarRange className="h-4 w-4 text-gray-400" />
            <SegmentedTabs tabs={PERIODS} value={period} onChange={handlePeriod} />
            {customOpen && (
              <form onSubmit={applyCustom} className="absolute left-0 top-full z-20 mt-2 w-full max-w-xs space-y-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-xl sm:left-auto sm:right-0">
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-xs text-gray-500">
                    De
                    <input type="date" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-2.5 py-1.5 text-sm text-gray-900 focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/15" />
                  </label>
                  <label className="text-xs text-gray-500">
                    Até
                    <input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-2.5 py-1.5 text-sm text-gray-900 focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/15" />
                  </label>
                </div>
                <button type="submit" className="w-full rounded-xl bg-accent py-2 text-sm font-medium text-white transition hover:bg-accent-hover">Aplicar período</button>
              </form>
            )}
          </div>

          <Panel title="Visão rápida" subtitle={isPeriodFiltered ? `Movimento das usuárias em ${periodLabel}` : 'Movimento das usuárias desde o início'}>
            <div className={`grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-6 ${isLoading ? 'animate-pulse' : ''}`}>
              <StatTile icon={Wallet} label="Faturamento" value={compactCurrency(totalTransacted)} caption="entradas lançadas" />
              <StatTile icon={Receipt} label="Lançamentos" value={formatNumber(totalTransactions)} caption="entradas no financeiro" />
              <StatTile icon={CalendarDays} label="Agendamentos" value={formatNumber(totalAppointments)} caption="sem contar bloqueios" />
              <StatTile icon={TrendingUp} label="Ticket médio" value={formatCurrency(ticket)} caption="por entrada" />
              <StatTile icon={Users} label={isPeriodFiltered ? 'Novas contas' : 'Contas'} value={formatNumber(isPeriodFiltered ? userStats.newInPeriod : userStats.total)} caption={isPeriodFiltered ? `${userStats.payingInPeriod} já assinam` : `${userStats.total} cadastradas`} />
              <StatTile icon={BadgeCheck} label="Assinantes" value={formatNumber(userStats.paying)} caption={`${conversion}% de conversão`} />
            </div>
          </Panel>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
            <Panel
              className="xl:col-span-2"
              icon={BarChart3}
              title="Movimento diário"
              subtitle={isPeriodFiltered ? periodLabel : 'Últimos 30 dias'}
              actions={
                <SegmentedTabs
                  value={chartMetric}
                  onChange={setChartMetric}
                  tabs={[{ key: 'revenue', label: 'Faturamento' }, { key: 'appointments', label: 'Agendamentos' }]}
                />
              }
            >
              {chartData.length ? (
                <BarChart
                  data={chartData}
                  valueKey={chartMetric}
                  format={chartMetric === 'revenue' ? compactCurrency : (v) => formatNumber(Math.round(v))}
                  tooltipExtra={chartMetric === 'revenue' ? (d) => `${d.transactions} lançamentos` : undefined}
                  color={chartMetric === 'revenue' ? '#10B981' : '#6D4AFE'}
                />
              ) : (
                <div className="flex h-[200px] flex-col items-center justify-center text-center">
                  <BarChart3 className="h-6 w-6 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">{isLoading ? 'Carregando...' : 'A série diária chega com a próxima versão da API.'}</p>
                </div>
              )}
            </Panel>

            <Panel icon={PieChart} title="Distribuição por plano" subtitle={`${formatNumber(planTotal)} contas`}>
              <div className="mb-5 flex h-3 overflow-hidden rounded-full bg-gray-100">
                {planItems.filter((p) => p.value).map((p) => (
                  <div key={p.key} title={`${p.label}: ${p.value}`} style={{ width: `${(p.value / Math.max(1, planTotal)) * 100}%`, backgroundColor: p.color }} />
                ))}
              </div>
              <HBarList items={planItems} total={planTotal} />
            </Panel>
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
            <Panel icon={UserPlus} title="Novos cadastros" subtitle="Por semana · últimas 12 semanas">
              <BarChart
                data={userStats.weeks}
                valueKey="signups"
                height={160}
                format={(v) => formatNumber(Math.round(v))}
                tooltipExtra={(d) => `${d.paying} já assinam`}
              />
            </Panel>
            <Panel icon={Shapes} title="Áreas de atuação" subtitle={`${userStats.withArea} contas responderam${isPeriodFiltered ? ' no período' : ''}`}>
              <HBarList items={userStats.areas.slice(0, 7)} total={userStats.withArea} emptyLabel="Ninguém respondeu ainda" />
            </Panel>
            <Panel icon={Megaphone} title="Como conheceram" subtitle={`${userStats.withSource} contas responderam${isPeriodFiltered ? ' no período' : ''}`}>
              <HBarList items={userStats.sources} total={userStats.withSource} emptyLabel="Ninguém respondeu ainda" />
            </Panel>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <RankingPanel
              icon={Trophy}
              title="Maior faturamento"
              subtitle={isPeriodFiltered ? periodLabel : 'Desde o início'}
              items={topTransactors}
              valueOf={(i) => i.totalValue || 0}
              onItemClick={(item) => item._id && setSelectedPerson(users.find((u) => u._id === item._id) || item)}
              emptyLabel="Nenhuma entrada encontrada"
              renderValue={(item) => (
                <>
                  <span className="tabular block text-sm font-semibold text-emerald-600">{formatCurrency(item.totalValue)}</span>
                  <span className="tabular block text-xs text-gray-500">{formatNumber(item.count)} entradas</span>
                </>
              )}
            />
            <RankingPanel
              icon={CalendarDays}
              title="Mais agendamentos"
              subtitle={isPeriodFiltered ? periodLabel : 'Desde o início'}
              items={topByAppointments}
              valueOf={(i) => i.count || 0}
              onItemClick={(item) => item._id && setSelectedPerson(users.find((u) => u._id === item._id) || item)}
              emptyLabel="Nenhum agendamento encontrado"
              renderValue={(item) => (
                <>
                  <span className="tabular block text-sm font-semibold text-accent">{formatNumber(item.count)}</span>
                  <span className="block text-xs text-gray-500">agendamentos</span>
                </>
              )}
            />
          </div>
        </div>
      </AppShell>

      <UserDetailsDrawer person={selectedPerson} onClose={() => setSelectedPerson(null)} />
    </>
  );
};
